import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useDrawerCloseGuard } from "./useDrawerCloseGuard";

type Dirty = boolean | (() => boolean);
type Entry = { dirty: () => boolean; busy: () => boolean };
type Scope = { register: (entry: Entry) => () => void; hasChanges: () => boolean; isBusy: () => boolean };
const DrawerDraftContext = createContext<Scope | null>(null);
export const DrawerDraftProvider = DrawerDraftContext.Provider;

/** Eigener Lebensraum einer gerouteten Arbeitsfläche, keine globale Sammlung fremder Formulare. */
export function useDrawerDraftScope(): Scope {
  const entries = useRef(new Set<Entry>());
  return useMemo(() => ({
    register: (entry: Entry) => { entries.current.add(entry); return () => { entries.current.delete(entry); }; },
    hasChanges: () => [...entries.current].some(entry => entry.dirty()),
    isBusy: () => [...entries.current].some(entry => entry.busy()),
  }), []);
}

/** Formulare/untergeordnete Drawer melden nur ihren tatsächlichen Entwurfs- und Speicherstand. */
export function useDrawerDraft(dirty: Dirty, active = true, busy: Dirty = false) {
  const scope = useContext(DrawerDraftContext);
  const latest = useRef({ dirty, busy });
  useLayoutEffect(() => { latest.current = { dirty, busy }; });
  useEffect(() => {
    if (!scope || !active) return;
    return scope.register({
      dirty: () => typeof latest.current.dirty === "function" ? latest.current.dirty() : latest.current.dirty,
      busy: () => typeof latest.current.busy === "function" ? latest.current.busy() : latest.current.busy,
    });
  }, [scope, active]);
}

/** Auch Rücksprungaktionen innerhalb einer eingebetteten Route schließen über den Arbeitsflächenvertrag. */
export function useDrawerWorkspaceClose(onClose: (() => void) | undefined) {
  const scope = useContext(DrawerDraftContext);
  const requestClose = useDrawerCloseGuard(() => { if (!scope?.isBusy()) onClose?.(); }, () => scope?.hasChanges() ?? false);
  if (!onClose) return undefined;
  return () => { if (!scope?.isBusy()) requestClose(); };
}
